/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Warren Dowey
 */

import { Injectable } from '@angular/core';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { ProjectImageModel } from '@app/models/project-image.model';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class ProjectImagesListService extends ApiBaseService {
  private _projectImagesList: ProjectImageModel[] = [];
  protected _endpoint = 'project_images';

  constructor(http: HttpClient, authUserService: AuthUserService) {
    super(http, authUserService);
  }

  get projectImagesList(): ProjectImageModel[] {
    return this._projectImagesList;
  }

  set projectImageList(value: ProjectImageModel[]) {
    this._projectImagesList = value;
  }

  /**
   *  Warren Dowey
   *
   *  getProjectImagesByProjectSlug
   *
   * : Get a list of projectImages based on the projectSlug
   *
   * @param projectSlug of type string
   *
   * @returns Void: gets the projectImages array with the projectSlug that meets the query
   */
  getProjectImagesByProjectSlug(projectSlug: string): ProjectImageModel[] {
    let tempList: ProjectImageModel[] = [];
    for (const image of this.projectImagesList) {
      if (image.projectSlug === projectSlug) {
        tempList.push(image);
      }
    }
    return tempList;
  }

  /**
   *  Warren Dowey
   *
   *  getProjectImageById
   *
   * : Get a projectImage based on the projectImageUuid by checking the list
   *
   * @param projectImageUuid of type string
   *
   * @returns ProjectImageModel
   */
  getProjectImageById(projectImageUuid: string): ProjectImageModel {
    if (!this.projectImagesList) {
      return;
    }
    for (const image of this.projectImagesList) {
      if (image.projectImagesUuid === projectImageUuid) {
        return image;
      }
    }
  }

  /**
   *   Warren Dowey
   *
   *  addtoList
   *
   * : handles the insert of a new projectImage into the list
   *
   * @param data of type ProjectImageModel
   *
   * @returns Void: inserts a new projectImage into the list
   */
  addToList(data: ProjectImageModel): void {
    this.projectImagesList.push(data);
  }
  /**
   *   Warren Dowey
   *
   *  removeFromList
   *
   * : deletes a projectImage from the list
   *
   * @param data of type ProjectImageModel
   *
   * @returns Void: deletes a projectImage
   */
  removeFromList(data: ProjectImageModel): void {
    let x = 0;
    if (data && this.projectImageList) {
      for (const image of this.projectImageList) {
        if (image.projectImagesUuid === data.projectImagesUuid) {
          this.projectImagesList.splice(x, 1);
        }
        x++;
      }
    }
  }
  /**
   *  Warren Dowey
   *
   *  updateInList
   *
   * : handles the update of a projectImage in the list
   *
   * @param data of type ProjectImageModel
   *
   * @returns Void: update the list with the data
   */
  updateInList(data: ProjectImageModel): void {
    for (let image of this.projectImagesList) {
      if (image.projectImagesUuid === data.projectImagesUuid) {
        image = data;
      }
    }
  }
  /**
   *  Warren Dowey
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type ProjectImageModel[]
   *
   * @returns Void
   */
  protected handleData(data: any): void {
    let projectImage: ProjectImageModel;
    this.projectImageList = [];
    super.handleData(data);
    for (const item of data) {
      projectImage = new ProjectImageModel();
      this.addToList(projectImage.fromJson(item));
    }
  }
}
